import React, { useState } from 'react';
import { Languages } from 'lucide-react';
import { api } from '../lib/api';
import { useLang } from '../contexts/LanguageContext';
import { Button } from './ui/button';
import { toast } from 'sonner';

/**
 * On-demand recipe translator. If the current language version of the recipe
 * content (ingredients + steps) is missing or identical to English, the user
 * can tap this button to call the AI translator. Backend caches the result
 * so subsequent visitors get it instantly.
 *
 * Props:
 *   recipe: the full recipe object
 *   onTranslated: callback(updatedRecipe) called when translation arrives
 */
export default function RecipeTranslate({ recipe, onTranslated }) {
  const { t, lang } = useLang();
  const [loading, setLoading] = useState(false);

  // English version is always present — no translation needed.
  if (lang === 'en') return null;

  // Heuristic: check if ingredients/steps are already localised.
  const firstStep = recipe?.steps_ar?.[0]?.text || recipe?.steps_en?.[0]?.text || '';
  const firstStepEn = recipe?.steps_en?.[0]?.text || '';
  const firstIngrName = recipe?.ingredients?.[0]?.[`name_${lang}`] || recipe?.ingredients?.[0]?.name_en || '';
  const firstIngrNameEn = recipe?.ingredients?.[0]?.name_en || '';

  const langStepKey = `steps_${lang}`;
  const stepsForLang = recipe?.[langStepKey];

  const looksTranslated = (
    (lang === 'ar')
      // For Arabic: steps already have arabic characters and differ from English version
      ? (firstStep !== firstStepEn && /[\u0600-\u06FF]/.test(firstStep))
      // For other languages: ingredient names differ from English
      : (firstIngrName !== firstIngrNameEn && firstIngrName.length > 0)
  );

  // If a translated payload for this language is already stored, hide the button.
  if (looksTranslated && stepsForLang && stepsForLang.length > 0) return null;

  const run = async () => {
    setLoading(true);
    try {
      const { data } = await api.post(`/recipes/${recipe.recipe_id}/translate`, { language: lang });
      if (data?.recipe) {
        onTranslated?.(data.recipe);
        toast.success(t('translated'));
      } else {
        toast.error('Error');
      }
    } catch (e) {
      toast.error('Error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="rounded-2xl bg-[#FBF1E0] dark:bg-neutral-800 border border-[#E29F48]/30 p-3 flex items-center justify-between gap-3" data-testid="translate-banner">
      <div className="flex items-center gap-2 text-sm text-[#5C4D44] dark:text-neutral-300">
        <Languages className="h-4 w-4 text-[#E29F48]" />
        <span>{t('translatePrompt')}</span>
      </div>
      <Button
        onClick={run}
        disabled={loading}
        data-testid="translate-btn"
        className="rounded-full bg-[#E29F48] hover:bg-[#cd8c33] text-white text-xs px-4 shrink-0"
      >
        {loading ? t('translating') : t('translateNow')}
      </Button>
    </div>
  );
}
